import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';
import { TokenService } from '@services/helpers/token.service';
import { filter } from 'rxjs';
import { CHAT_FEATURE_FLAG } from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { isChatUrl } from '../../utils/chat-route.util';
import { ChatConversationComponent } from '../chat-conversation/chat-conversation.component';
import { ChatForwardDialogComponent } from '../chat-forward-dialog/chat-forward-dialog.component';
import { ChatGroupDialogComponent, ChatGroupDialogMode } from '../chat-group-dialog/chat-group-dialog.component';
import { ChatGroupInfoComponent } from '../chat-group-info/chat-group-info.component';
import { ChatIncomingCallComponent } from '../chat-incoming-call/chat-incoming-call.component';
import { ChatQuickMessageDialogComponent } from '../chat-quick-message-dialog/chat-quick-message-dialog.component';
import { ChatStatusMenuComponent } from '../chat-status-menu/chat-status-menu.component';
import { ChatToastComponent } from '../chat-toast/chat-toast.component';
import { ChatSidebarComponent } from '../chat-sidebar/chat-sidebar.component';

/**
 * The widget's three sizes, driven by standard window controls: `mini` is minimised to a header bar
 * in the corner, `mid` a floating panel with the list and thread side by side, `full` the same two
 * columns covering the viewport.
 */
export type ChatPanelSize = 'mini' | 'mid' | 'full';
type ChatExpandedSize = Exclude<ChatPanelSize, 'mini'>;

const PANEL_SIZE_STORAGE_KEY = 'remmi.chat.panelSize';

/**
 * The chat popover, mounted once in the app shell.
 *
 * A floating panel in three sizes ({@link ChatPanelSize}) holding the same sidebar and
 * conversation components the full page uses. Because everything reads {@link ChatStore},
 * switching size keeps the conversation you were already in.
 *
 * It is opened from {@link ChatTriggerComponent} in the header, not from a button here. The two
 * are separate because the top bar is `position: fixed` with a `z-index` and so forms a stacking
 * context — a panel rendered inside it is trapped at the bar's layer and cannot cover the page.
 * This component stays in the shell where nothing boxes it in.
 *
 * Gated on the `chat` feature flag and on actually being logged in — the shell also mounts this
 * on public routes, where there is no office and no socket.
 */
@Component({
  selector: 'app-chat-launcher',
  standalone: true,
  imports: [
    CommonModule,
    ChatSidebarComponent,
    ChatConversationComponent,
    ChatGroupInfoComponent,
    ChatGroupDialogComponent,
    ChatForwardDialogComponent,
    ChatToastComponent,
    ChatIncomingCallComponent,
    ChatQuickMessageDialogComponent,
    ChatStatusMenuComponent,
  ],
  templateUrl: './chat-launcher.component.html',
  styleUrls: ['./chat-launcher.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatLauncherComponent implements OnInit {
  readonly store = inject(ChatStore);
  private readonly flags = inject(FeatureFlagService);
  private readonly tokens = inject(TokenService);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('panelEl') private panelElRef?: ElementRef<HTMLElement>;

  /**
   * Drag-to-reposition, set from {@link startDrag}. `null` until the panel is first dragged, so it
   * stays on the `right/bottom` anchor `chat-launcher.component.scss` positions it with — these two
   * only ever get a value once the user has moved it themselves.
   */
  dragLeft: number | null = null;
  dragTop: number | null = null;

  private dragging = false;
  private dragPointerStart = { x: 0, y: 0 };
  private dragPanelStart = { left: 0, top: 0 };

  /**
   * Mid or Full, remembered per browser so the widget reopens at the size the user last picked.
   * Minimised is never remembered — opening chat and getting a bar back would read as broken.
   */
  private expandedSize: ChatExpandedSize = this.readStoredSize();
  readonly panelSize = signal<ChatPanelSize>(this.expandedSize);
  private wasOpen = false;

  constructor() {
    // Reopening chat (from the top bar) after closing it while minimised brings it back expanded.
    effect(
      () => {
        const isOpen = this.store.panelOpen();
        if (isOpen && !this.wasOpen && untracked(this.panelSize) === 'mini') {
          this.panelSize.set(this.expandedSize);
        }
        this.wasOpen = isOpen;
      },
      { allowSignalWrites: true }
    );
  }

  enabled = false;
  isMobile = false;
  dialogMode: ChatGroupDialogMode | null = null;
  /** Which tab the group dialog opens on — "Add Members" should not land on Details. */
  dialogTab: 'details' | 'members' | 'settings' = 'details';
  /** Group Info, shown as an overlay over the panel: 380px has no room for a third column. */
  infoOpen = false;

  /**
   * Hidden on the full chat page — two chats on one screen is just confusing.
   *
   * A field kept in step with the router, **not** a getter reading `router.url`. This component is
   * OnPush and lives in the app shell, so it is never re-created and a navigation does not mark it
   * dirty: a getter was evaluated while `/chat` was open, latched to `true`, and the launcher then
   * stayed missing on every page the user visited afterwards until something unrelated happened to
   * trigger change detection.
   */
  onChatPage = false;

  ngOnInit(): void {
    this.updateViewport();
    this.enabled = this.flags.isFeatureEnabled(CHAT_FEATURE_FLAG) && this.tokens.isUserloggedIn();
    this.onChatPage = isChatUrl(this.router.url);

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        const next = isChatUrl(event.urlAfterRedirects || event.url);
        if (next !== this.onChatPage) {
          this.onChatPage = next;
          this.cdr.markForCheck();
        }
      });

    if (this.enabled) {
      // Load the sidebar up front so the badge is accurate before the widget is ever opened.
      this.store.init();
    }
  }

  close(): void {
    this.store.closePanel();
  }

  /** Minimised to the header bar. A phone has no minimised state — the panel is the whole screen. */
  get isMini(): boolean {
    return !this.isMobile && this.panelSize() === 'mini';
  }

  /** List and thread side by side — every desktop size except minimised. A phone stays one column. */
  get isSplit(): boolean {
    return !this.isMobile && !this.isMini;
  }

  /** Only Mid floats free; the minimised bar docks in the corner and Full is pinned to the viewport. */
  get isDraggable(): boolean {
    return !this.isMobile && this.panelSize() === 'mid';
  }

  minimise(): void {
    this.panelSize.set('mini');
    this.infoOpen = false;
  }

  /** Back from the bar to whichever expanded size it was minimised from. */
  restore(): void {
    this.setSize(this.expandedSize);
  }

  /** The minimised bar is one big restore button — except its own Restore/Close buttons. */
  onBarClick(event: MouseEvent): void {
    // composedPath(), not target.closest(): the Minimise click re-renders before it bubbles here, so
    // its button is already detached and closest() found no toolbar — the bar read the same click as
    // "restore" and undid the minimise on the spot. The path is fixed at dispatch and survives that.
    const fromTools = event.composedPath().some(node => (node as HTMLElement)?.classList?.contains('chat-launcher__header-tools'));
    if (this.isMini && !fromTools) {
      this.restore();
    }
  }

  setSize(size: ChatExpandedSize): void {
    this.expandedSize = size;
    try {
      localStorage.setItem(PANEL_SIZE_STORAGE_KEY, size);
    } catch {
      // Storage blocked (private window, site data off) — the size just isn't remembered.
    }
    if (this.panelSize() === size) {
      return;
    }
    this.panelSize.set(size);
    this.infoOpen = false;
    // A dragged panel restored to Mid can now run off the screen edge; pull it back in once the new
    // size has rendered.
    if (this.isDraggable && this.dragLeft !== null && this.dragTop !== null) {
      requestAnimationFrame(() => {
        if (this.panelElRef && this.isDraggable && this.dragLeft !== null && this.dragTop !== null) {
          this.applyDragPosition(this.dragLeft, this.dragTop);
        }
      });
    }
  }

  /** An old stored `mini` (when Mini was the one-column panel) falls back to Mid. */
  private readStoredSize(): ChatExpandedSize {
    try {
      return localStorage.getItem(PANEL_SIZE_STORAGE_KEY) === 'full' ? 'full' : 'mid';
    } catch {
      return 'mid';
    }
  }

  back(): void {
    this.infoOpen = false;
    this.store.closeConversation();
  }

  toggleInfo(): void {
    this.infoOpen = !this.infoOpen;
  }

  closeInfo(): void {
    this.infoOpen = false;
  }

  openCreate(): void {
    this.dialogMode = 'create';
    this.dialogTab = 'details';
  }

  /** The info panel's Edit/Add Members buttons land on the matching tab, as they do on the page. */
  openManage(tab: 'details' | 'members' | 'settings' = 'details'): void {
    this.dialogMode = 'manage';
    this.dialogTab = tab;
    this.infoOpen = false;
  }

  closeDialog(): void {
    this.dialogMode = null;
  }

  @HostListener('window:resize')
  onResize(): void {
    const was = this.isMobile;
    this.updateViewport();
    if (was !== this.isMobile) {
      this.cdr.markForCheck();
    }
    if (this.isDraggable && this.dragLeft !== null && this.dragTop !== null && this.panelElRef) {
      this.applyDragPosition(this.dragLeft, this.dragTop);
    }
  }

  private updateViewport(): void {
    this.isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  }

  /**
   * Starts a drag from the header — the header's own buttons (status, sizes, close) are excluded
   * so they keep working as plain clicks. Disabled on mobile and in Full, where the panel fills the
   * screen and there is nowhere to drag it to.
   */
  startDrag(event: PointerEvent): void {
    if (!this.isDraggable || !this.panelElRef) {
      return;
    }
    if ((event.target as HTMLElement).closest('.chat-launcher__header-tools')) {
      return;
    }
    const rect = this.panelElRef.nativeElement.getBoundingClientRect();
    this.dragPanelStart = { left: rect.left, top: rect.top };
    this.dragPointerStart = { x: event.clientX, y: event.clientY };
    this.dragging = true;
    // Stops the header text/icons from being selected while the pointer drags across them.
    event.preventDefault();
  }

  @HostListener('document:pointermove', ['$event'])
  onDragMove(event: PointerEvent): void {
    if (!this.dragging || !this.panelElRef) {
      return;
    }
    const left = this.dragPanelStart.left + (event.clientX - this.dragPointerStart.x);
    const top = this.dragPanelStart.top + (event.clientY - this.dragPointerStart.y);
    this.applyDragPosition(left, top);
  }

  @HostListener('document:pointerup')
  onDragEnd(): void {
    this.dragging = false;
  }

  /** Written straight to the panel's own style for a jank-free drag, and mirrored onto the bound fields so a reopen (or a resize clamp) starts from the same spot. */
  private applyDragPosition(left: number, top: number): void {
    const panel = this.panelElRef!.nativeElement;
    const maxLeft = Math.max(0, window.innerWidth - panel.offsetWidth);
    const maxTop = Math.max(0, window.innerHeight - panel.offsetHeight);
    this.dragLeft = Math.min(Math.max(left, 0), maxLeft);
    this.dragTop = Math.min(Math.max(top, 0), maxTop);
    panel.style.left = `${this.dragLeft}px`;
    panel.style.top = `${this.dragTop}px`;
  }
}
